import React from 'react';
import { Loader2 } from 'lucide-react';
import { zoom, isIdentity, IDENTITY } from '../../../lib/photoManualTransform';
import { transformString } from '../../../lib/photoRender';
import {
  useDragPan, OnPhotoControls, ZoomBadge, PanelBackdrop, GridOverlay, PhotoLabel,
  canonicalImgStyle, formatMetricLine,
} from './PanelBits';
import { EmptySlot } from './SideBySidePair';

// One pair as a single blended panel. opacity and blend are owned by the
// caller so two panels stay in step.
export default function OverlayPanel({ pair, opacity, blend, gridOn, labels, testIdSuffix = '', computing = false }) {
  const { before, after } = pair;
  const drag = useDragPan(before.setManual, true);
  if (!before.url || !after.url) {
    return <EmptySlot label={labels.missing} testId={`cmp-panel-overlay${testIdSuffix}`} />;
  }
  return (
    <div data-testid={`cmp-panel-overlay${testIdSuffix}`} {...drag}
         className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black ring-1 ring-white/10 shadow-xl">
      <PanelBackdrop url={after.url} />
      <img src={after.url} alt="" data-testid="cmp-img"
           style={canonicalImgStyle(after.canonical)} draggable={false} />
      <img src={before.url} alt="" data-testid="cmp-img" className="absolute inset-0"
           style={{
             ...canonicalImgStyle(before.canonical, transformString(null, before.manual)),
             opacity: 1 - opacity,
             mixBlendMode: blend === 'difference' ? 'difference' : 'normal',
           }}
           draggable={false} />
      {gridOn && <GridOverlay />}
      <PhotoLabel position="top-left" tone="dark"
                  metric={formatMetricLine(before.weightKg, before.bodyFat)} label={labels.before} />
      <PhotoLabel position="top-right" tone="accent"
                  metric={formatMetricLine(after.weightKg, after.bodyFat)} label={labels.after} />
      <ZoomBadge manual={before.manual} />
      <OnPhotoControls
        onZoomOut={() => before.setManual((m) => zoom(m, -1, before.canonical))}
        onZoomIn={() => before.setManual((m) => zoom(m, +1, before.canonical))}
        onAuto={before.autoMatch}
        autoTitle={labels.mismatch}
        onReset={isIdentity(before.manual) ? null : () => before.setManual(IDENTITY)}
      />

      {computing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );
}
